import { useEffect } from 'react';

type SeoProps = {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  noIndex?: boolean;
};

function upsertMeta(selector: string, attrs: Record<string, string>) {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement('meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
}

function upsertLinkRelCanonical(href: string) {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

export default function Seo({ title, description, canonical, ogImage, noIndex }: SeoProps) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    if (description) {
      upsertMeta('meta[name="description"]', { name: 'description', content: description });
    }

    const url = canonical || window.location.origin + window.location.pathname;
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title || prevTitle });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description || '' });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: url });
    if (ogImage) upsertMeta('meta[property="og:image"]', { property: 'og:image', content: ogImage });

    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    if (title) upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
    if (description) upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: description });
    if (ogImage) upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: ogImage });

    if (canonical) upsertLinkRelCanonical(url);

    if (noIndex) {
      upsertMeta('meta[name="robots"]', { name: 'robots', content: 'noindex, nofollow' });
    }

    return () => {
      // restore title on unmount
      // document.title = prevTitle; // usually undesirable to revert across route changes
    };
  }, [title, description, canonical, ogImage, noIndex]);

  return null;
}

